/**
 * Web 端缓存治理（仅 web 构建引入，见 main.ts 的 // #ifdef WEB）
 *
 * 典型症状：发版后用户打开的还是旧界面（index.html 被浏览器 / CDN 缓存），
 * 或者旧 index.html 引用的 chunk 已被删除导致白屏。
 *
 * 对策：
 *  1. 构建时把版本号注入 index.html（window.__APP_VERSION__）并生成 version.json
 *     （见 config/plugins/web-cache.ts）；
 *  2. 启动 / 切回前台 / 定时拉取 version.json，发现新版本就提示或直接强刷；
 *  3. 资源加载失败、动态 chunk 404 时自动强刷一次自愈；
 *  4. 强刷统一走 location.replace(url?_v=时间戳)：URL 变了才能绕开缓存，
 *     单纯 location.reload() 往往依旧命中缓存。
 */
import { confirmAction } from '@/utils/confirmRemove'

/** 两次强刷的最小间隔（毫秒），防止刷新失败时死循环 */
const RELOAD_MIN_GAP = 10 * 1000
/** 定时检查间隔 */
const CHECK_INTERVAL = 5 * 60 * 1000
/** 两次检查的最小间隔，避免频繁切前台时狂刷请求 */
const CHECK_MIN_GAP = 60 * 1000

let checking = false
let prompted = false
let lastCheckAt = 0

/** 当前页面版本（构建时注入到 index.html） */
export function appVersion(): string {
  return String((window as any).__APP_VERSION__ || '')
}

/** 站点 base（vite base，如 / 或 /app/） */
function baseUrl(): string {
  const base = String((import.meta as any).env?.BASE_URL || '/')
  return base.endsWith('/') ? base : `${base}/`
}

/** 记录本次强刷时间，返回是否允许继续刷新 */
function allowReload(): boolean {
  let last = 0
  try {
    last = Number(sessionStorage.getItem('webHardReloadAt') || 0)
  } catch {
    last = 0
  }
  const now = Date.now()
  if (now - last < RELOAD_MIN_GAP) return false
  try {
    sessionStorage.setItem('webHardReloadAt', String(now))
  } catch {
    /* 忽略：无痕模式下 sessionStorage 可能不可用 */
  }
  return true
}

/** 强刷：URL 带随机参数，绕开对同名 URL 的缓存 */
export function hardReload(): void {
  if (!allowReload()) return
  try {
    const url = new URL(window.location.href)
    url.searchParams.set('_v', String(Date.now()))
    window.location.replace(url.href)
  } catch {
    window.location.reload()
  }
}

/** 拉服务端版本（带时间戳 + no-store，自身不进缓存） */
async function fetchRemoteVersion(): Promise<string> {
  const res = await fetch(`${baseUrl()}version.json?_t=${Date.now()}`, {
    cache: 'no-store',
    headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' }
  })
  if (!res.ok) return ''
  const data = await res.json()
  return String(data?.version || '')
}

/**
 * 检查是否有新版本。
 * @param autoReload true：静默强刷（刚打开页面、用户还没填内容时用）；
 *                   false：弹窗让用户确认后再刷（避免打断正在填写的创作内容）。
 */
export async function checkAppUpdate(autoReload = false): Promise<boolean> {
  const now = Date.now()
  if (checking || now - lastCheckAt < CHECK_MIN_GAP) return false
  lastCheckAt = now
  checking = true
  try {
    const remote = await fetchRemoteVersion()
    // 取不到（如本地 dev 没有 version.json）或版本一致：无需处理
    if (!remote || remote === appVersion()) return false
    if (prompted) return true
    prompted = true
    if (autoReload) {
      hardReload()
      return true
    }
    confirmAction('检测到新版本，是否立即更新？更新会重新加载页面', '发现新版本', '立即更新').then((ok) => {
      if (ok) hardReload()
    })
    return true
  } catch {
    return false
  } finally {
    checking = false
  }
}

/** 资源加载失败（旧 html 引用了已被删除的 chunk / 样式）→ 强刷自愈 */
function onAssetError(e: Event) {
  const tag = (e?.target as HTMLElement | null)?.tagName
  if (tag === 'SCRIPT' || tag === 'LINK') hardReload()
}

/** 动态 import 失败（路由跳转时报 Failed to fetch dynamically imported module）→ 强刷自愈 */
function onRejection(e: PromiseRejectionEvent) {
  const msg = String((e?.reason as any)?.message || e?.reason || '')
  if (msg.includes('dynamically imported module') || msg.includes('Loading chunk')) hardReload()
}

/** web 启动即挂载（main.ts 中调用） */
export function initWebCacheGuard(): void {
  // 冷启动稍等再查：此刻用户还没填内容，有新版本直接静默刷新
  setTimeout(() => checkAppUpdate(true), 3000)
  setInterval(() => checkAppUpdate(false), CHECK_INTERVAL)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) checkAppUpdate(false)
  })
  // 从 bfcache（前进后退缓存）回来时页面是旧快照
  window.addEventListener('pageshow', (e: any) => {
    if (e?.persisted) checkAppUpdate(true)
  })
  window.addEventListener('error', onAssetError, true)
  window.addEventListener('unhandledrejection', onRejection)
  window.addEventListener('vite:preloadError', () => hardReload())
}
